import { useState } from 'react';
import { getStudentData } from '../assets/data';
import { getLessonListForStudent, getLessonsFromLocalStorage, saveLessonsToLocalStorage } from '../utils/lessonsService';
import { saveScheduleItem, deleteScheduleItem } from '../utils/scheduleService';

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). Owns the Add-lesson and Edit-schedule-lesson
// modals: their form state, validation, and save/delete against the
// schedule table.
//
// handleSaveScheduleLesson's auto-charge/reverse-charge block is the same
// sensitive logic that caused several payment bugs earlier in this
// project's life — see paymentsRef's own comment in the caller for why it,
// not loadPayments(), is the correct read here. Left untouched on purpose.
export function useScheduleLessonModals({
  studentId,
  isNew,
  studentData,
  showCustomModalConfig,
  updateUpcomingLessons,
  setStudentLessons,
  paymentsKey,
  paymentsRef,
  studentDataRef,
  savePayments,
}) {
  // Add lesson to schedule
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [selectedDateForLesson, setSelectedDateForLesson] = useState(null);
  const [addLessonItemType, setAddLessonItemType] = useState('lesson');
  const [addLessonTitle, setAddLessonTitle] = useState('');
  const [addLessonFrom, setAddLessonFrom] = useState('09:00');
  const [addLessonTo, setAddLessonTo] = useState('10:00');
  const [addLessonRepeatWeekly, setAddLessonRepeatWeekly] = useState(false);
  const [addLessonAllDay, setAddLessonAllDay] = useState(false);
  const [addLessonErrors, setAddLessonErrors] = useState([]);
  const [addLessonSaving, setAddLessonSaving] = useState(false);

  // Edit schedule lesson
  const [showEditScheduleLessonModal, setShowEditScheduleLessonModal] = useState(false);
  const [editingScheduleLesson, setEditingScheduleLesson] = useState(null);
  const [editScheduleLessonTitle, setEditScheduleLessonTitle] = useState('');
  const [editScheduleLessonDate, setEditScheduleLessonDate] = useState('');
  const [editScheduleLessonFrom, setEditScheduleLessonFrom] = useState('');
  const [editScheduleLessonTo, setEditScheduleLessonTo] = useState('');
  const [editScheduleLessonAllDay, setEditScheduleLessonAllDay] = useState(false);
  const [editScheduleLessonStatus, setEditScheduleLessonStatus] = useState('in_progress');
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [editScheduleLessonSaving, setEditScheduleLessonSaving] = useState(false);
  const [deleteScheduleLessonSaving, setDeleteScheduleLessonSaving] = useState(false);

  const updateToTime = (newFromTime) => {
    const [hours, minutes] = newFromTime.split(':').map(Number);
    const endHours = hours + 1;
    return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Функция валидации для добавления урока
  const validateAddLessonForm = () => {
    const newErrors = [];

    // Проверка времени (только если не all-day)
    if (!addLessonAllDay) {
      if (!addLessonFrom || !addLessonTo) {
        newErrors.push('Please select both start and end time');
      } else if (addLessonFrom >= addLessonTo) {
        newErrors.push('End time must be after start time');
      }
    }

    // Проверка названия для событий
    if (addLessonItemType === 'event' && !addLessonTitle.trim()) {
      newErrors.push('Please enter an event title');
    }

    setAddLessonErrors(newErrors);
    return newErrors.length === 0;
  };

  // Функция для добавления урока в календарь (как в Schedule.js)
  const handleAddLessonToSchedule = async () => {
    if (!validateAddLessonForm()) {
      return;
    }

    if (!selectedDateForLesson) {
      showCustomModalConfig({
        title: 'Error',
        message: 'Please select a date.',
        type: 'error'
      });
      return;
    }

    setAddLessonSaving(true);
    try {
      const currentStudent = getStudentData()[studentId];
      const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;

      // Создаем дату без проблем с timezone
      const targetDate = selectedDateForLesson instanceof Date ? selectedDateForLesson : new Date(selectedDateForLesson);
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      const baseDate = new Date(year, targetDate.getMonth(), targetDate.getDate());
      let itemsToAdd = [];
      let recurringId = addLessonRepeatWeekly ? `series_${Date.now()}` : undefined;

      // Определяем title
      let itemTitle = addLessonTitle;
      if (addLessonItemType === 'lesson') {
        itemTitle = addLessonTitle || `${studentData.name || 'Student'}'s Lesson`;
      }

      if (addLessonRepeatWeekly) {
        for (let i = 0; i < 12; i++) {
          const itemDate = new Date(baseDate);
          itemDate.setDate(baseDate.getDate() + i * 7);
          itemsToAdd.push({
            type: addLessonItemType,
            studentId: addLessonItemType === 'lesson' ? studentIdForLessons : null,
            title: itemTitle,
            date: `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}-${String(itemDate.getDate()).padStart(2, '0')}`,
            from: addLessonAllDay ? null : addLessonFrom,
            to: addLessonAllDay ? null : addLessonTo,
            allDay: addLessonAllDay,
            status: addLessonItemType === 'lesson' ? 'in_progress' : 'in_progress',
            recurringId
          });
        }
      } else {
        itemsToAdd.push({
          type: addLessonItemType,
          studentId: addLessonItemType === 'lesson' ? studentIdForLessons : null,
          title: itemTitle,
          date: dateString,
          from: addLessonAllDay ? null : addLessonFrom,
          to: addLessonAllDay ? null : addLessonTo,
          allDay: addLessonAllDay,
          status: addLessonItemType === 'lesson' ? 'in_progress' : 'in_progress',
        });
      }

      // Сохраняем каждый элемент в Supabase
      const savePromises = itemsToAdd.map(async (item) => {
        try {
          console.log('💾 Saving item to Supabase:', { ...item, status: item.status });
          const saved = await saveScheduleItem(item);
          console.log('✅ Saved to Supabase:', { ...saved, status: saved.status });
          return saved;
        } catch (error) {
          console.error('❌ Error saving to Supabase:', error);
          throw error;
        }
      });

      await Promise.all(savePromises);

      // Обновляем список уроков, затем закрываем модалку
      await updateUpcomingLessons();

      setShowAddLessonModal(false);
      setSelectedDateForLesson(null);
      setAddLessonItemType('lesson');
      setAddLessonTitle('');
      setAddLessonFrom('09:00');
      setAddLessonTo('10:00');
      setAddLessonRepeatWeekly(false);
      setAddLessonAllDay(false);
      setAddLessonErrors([]);

      showCustomModalConfig({
        title: '✅ Lesson Added!',
        message: `Lesson has been added to schedule${addLessonRepeatWeekly ? ' (12 weeks)' : ''} for ${dateString} at ${addLessonFrom} - ${addLessonTo}.`,
        type: 'success',
        confirmText: 'OK'
      });
    } catch (error) {
      console.error('Error adding lesson to schedule:', error);
      showCustomModalConfig({
        title: 'Error',
        message: `Failed to add lesson: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setAddLessonSaving(false);
    }
  };

  // Функция для сохранения изменений урока из расписания
  const handleSaveScheduleLesson = async () => {
    if (!editingScheduleLesson) return;

    if (!editScheduleLessonDate) {
      showCustomModalConfig({
        title: 'Error',
        message: 'Please select a date.',
        type: 'error'
      });
      return;
    }

    if (!editScheduleLessonAllDay && (!editScheduleLessonFrom || !editScheduleLessonTo)) {
      showCustomModalConfig({
        title: 'Error',
        message: 'Please enter time range.',
        type: 'error'
      });
      return;
    }

    setEditScheduleLessonSaving(true);
    try {
      const currentStudent = getStudentData()[studentId];
      const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;

      const updatedLesson = {
        ...editingScheduleLesson,
        title: editScheduleLessonTitle || `${studentData.name || 'Student'}'s Lesson`,
        date: editScheduleLessonDate,
        from: editScheduleLessonAllDay ? null : editScheduleLessonFrom,
        to: editScheduleLessonAllDay ? null : editScheduleLessonTo,
        timeFrom: editScheduleLessonAllDay ? null : editScheduleLessonFrom,
        timeTo: editScheduleLessonAllDay ? null : editScheduleLessonTo,
        allDay: editScheduleLessonAllDay,
        status: editScheduleLessonStatus,
        studentId: studentIdForLessons
      };

      await saveScheduleItem(updatedLesson);

      // Auto-charge / reverse-charge when lesson status changes to/from done
      if (paymentsKey) {
        // paymentsRef, not loadPayments() — always mirrors the hook's
        // actual current payments state, see paymentsRef's own comment.
        const existingPayments = paymentsRef.current;
        let next = null;
        const prevStatus = editingScheduleLesson.status;
        const lessonIdForPayment = editingScheduleLesson.id;
        if (editScheduleLessonStatus === 'done' && prevStatus !== 'done') {
          const priceAmt = parseFloat(studentDataRef.current.priceAmount) || 0;
          if (priceAmt > 0) {
            next = [...existingPayments, {
              id: Date.now(),
              date: new Date().toISOString(),
              amount: -priceAmt,
              type: 'charge',
              lessonId: lessonIdForPayment,
              note: `${updatedLesson.title || 'Lesson'} (auto)`,
            }];
          }
        } else if (editScheduleLessonStatus !== 'done' && prevStatus === 'done') {
          // Only write if a charge was actually removed — see the same
          // guard in handleLessonStatusUpdate above for why.
          const filtered = existingPayments.filter(p => !(p.lessonId === lessonIdForPayment && p.type === 'charge'));
          if (filtered.length !== existingPayments.length) next = filtered;
        }
        if (next) {
          savePayments(next);
        }
      }

      // Закрываем модальное окно
      setShowEditScheduleLessonModal(false);
      setEditingScheduleLesson(null);
      setEditScheduleLessonTitle('');
      setEditScheduleLessonDate('');
      setEditScheduleLessonFrom('');
      setEditScheduleLessonTo('');
      setEditScheduleLessonAllDay(false);
      setEditScheduleLessonStatus('in_progress');

      // Обновляем upcoming lessons и основную таблицу
      setTimeout(() => {
        updateUpcomingLessons();
        // Обновляем список уроков для основной таблицы
        if (studentId && studentId !== "new" && !isNew) {
          getLessonListForStudent(studentIdForLessons).then(lessons => {
            setStudentLessons(lessons);
          }).catch(error => {
            console.error('Error loading lessons:', error);
          });
        }
      }, 500);

      showCustomModalConfig({
        title: '✅ Lesson Updated!',
        message: 'Lesson has been updated in schedule.',
        type: 'success',
        confirmText: 'OK'
      });
    } catch (error) {
      console.error('Error updating lesson in schedule:', error);
      showCustomModalConfig({
        title: 'Error',
        message: `Failed to update lesson: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setEditScheduleLessonSaving(false);
    }
  };

  // Функция для удаления урока из расписания (подтверждение через кастомное модальное окно)
  const handleDeleteScheduleLesson = () => {
    if (!editingScheduleLesson) return;
    showCustomModalConfig({
      title: 'Delete lesson from schedule?',
      message: 'Are you sure you want to delete this lesson from schedule?',
      showCancel: true,
      cancelText: 'Cancel',
      confirmText: 'Delete',
      onConfirm: () => performDeleteScheduleLesson(),
    });
  };

  const performDeleteScheduleLesson = async () => {
    if (!editingScheduleLesson) return;
    setDeleteScheduleLessonSaving(true);
    try {
      await deleteScheduleItem(editingScheduleLesson.id);

      // Также удаляем из localStorage, если там есть
      const allLessonsData = getLessonsFromLocalStorage();
      const updatedLessonsData = allLessonsData.filter(lesson => lesson.id !== editingScheduleLesson.id);
      saveLessonsToLocalStorage(updatedLessonsData);

      // Закрываем модальное окно
      setShowEditScheduleLessonModal(false);
      setEditingScheduleLesson(null);
      setEditScheduleLessonTitle('');
      setEditScheduleLessonDate('');
      setEditScheduleLessonFrom('');
      setEditScheduleLessonTo('');
      setEditScheduleLessonAllDay(false);
      setEditScheduleLessonStatus('in_progress');

      // Обновляем upcoming lessons и основную таблицу
      setTimeout(() => {
        updateUpcomingLessons();
        // Обновляем список уроков для основной таблицы
        const currentStudent = getStudentData()[studentId];
        const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
        if (studentId && studentId !== "new" && !isNew && studentIdForLessons) {
          getLessonListForStudent(studentIdForLessons).then(lessons => {
            setStudentLessons(lessons);
          }).catch(error => {
            console.error('Error loading lessons:', error);
          });
        }
      }, 500);

      showCustomModalConfig({
        title: '✅ Lesson Deleted!',
        message: 'Lesson has been deleted from schedule.',
        type: 'success',
        confirmText: 'OK'
      });
    } catch (error) {
      console.error('Error deleting lesson from schedule:', error);
      showCustomModalConfig({
        title: 'Error',
        message: `Failed to delete lesson: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setDeleteScheduleLessonSaving(false);
    }
  };

  return {
    showAddLessonModal, setShowAddLessonModal,
    selectedDateForLesson, setSelectedDateForLesson,
    addLessonItemType, setAddLessonItemType,
    addLessonTitle, setAddLessonTitle,
    addLessonFrom, setAddLessonFrom,
    addLessonTo, setAddLessonTo,
    addLessonRepeatWeekly, setAddLessonRepeatWeekly,
    addLessonAllDay, setAddLessonAllDay,
    addLessonErrors, setAddLessonErrors,
    addLessonSaving,
    showEditScheduleLessonModal, setShowEditScheduleLessonModal,
    editingScheduleLesson, setEditingScheduleLesson,
    editScheduleLessonTitle, setEditScheduleLessonTitle,
    editScheduleLessonDate, setEditScheduleLessonDate,
    editScheduleLessonFrom, setEditScheduleLessonFrom,
    editScheduleLessonTo, setEditScheduleLessonTo,
    editScheduleLessonAllDay, setEditScheduleLessonAllDay,
    editScheduleLessonStatus, setEditScheduleLessonStatus,
    statusChangeLoading, setStatusChangeLoading,
    editScheduleLessonSaving,
    deleteScheduleLessonSaving,
    updateToTime,
    handleAddLessonToSchedule,
    handleSaveScheduleLesson,
    handleDeleteScheduleLesson,
  };
}
